import { useRef, useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Check, MessageSquare, Zap, Star, ChevronRight } from "lucide-react";
import { FadeUp, CardHover } from "../hooks/motion";
import { CALCOM_DEMO_URL } from "../config/demo";

/* ── Static animation timing (module-level — stable, no deps needed) */
const SMS_DELAYS = [500, 1400, 2300, 3500, 4300];

/* ── Vertical SMS Mockup ────────────────────────────────── */
const VerticalSMSMockup = ({ quoteCard }) => {
  const [visible, setVisible] = useState(0);

  // msgs depends on quoteCard prop — memoized so the reference is stable
  const msgs = useMemo(() => [
    { type: "in", text: `Hi, I need a quote for ${quoteCard.service}` },
    { type: "out", text: "On it! Here's your quote:" },
    { type: "quote", ...quoteCard },
    { type: "in", text: "Looks perfect. I'll take it!" },
    { type: "status", text: "Job Created ✓" },
  ], [quoteCard]);

  const prefersReduced = useRef(
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  useEffect(() => {
    if (prefersReduced.current) { setVisible(msgs.length); return; }
    if (visible >= msgs.length) {
      const t = setTimeout(() => setVisible(0), 2500);
      return () => clearTimeout(t);
    }
    const gap = visible === 0 ? SMS_DELAYS[0] : SMS_DELAYS[visible] - SMS_DELAYS[visible - 1];
    const t = setTimeout(() => setVisible(v => v + 1), gap);
    return () => clearTimeout(t);
  // SMS_DELAYS is module-level (stable); msgs.length is included as it is read inside the effect
  }, [visible, msgs.length]);

  return (
    <div className="relative mx-auto w-full max-w-[300px]">
      {/* Device shell — fixed dimensions, never resizes during animation */}
      <div className="relative bg-[#050507] rounded-[2.75rem] p-2.5 border border-[#26263a] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.7)]">
        <div className="pointer-events-none absolute inset-0 rounded-[2.75rem] ring-1 ring-inset ring-white/[0.04]" />
        {/* Screen — fixed height reserved immediately */}
        <div
          className="relative rounded-[2.25rem] bg-[#0d0d14] overflow-hidden flex flex-col"
          style={{ height: "420px" }}
        >
          {/* Status bar with notch */}
          <div className="relative h-9 flex-shrink-0 flex items-center justify-center">
            <div className="h-6 w-28 bg-black rounded-full flex items-center justify-center gap-2">
              <span className="w-8 h-1 rounded-full bg-[#1E1E2E]" />
              <span className="w-2 h-2 rounded-full bg-[#1E1E2E]" />
            </div>
          </div>

          {/* Header */}
          <div className="flex items-center gap-2 px-4 pb-3 border-b border-[#1E1E2E] flex-shrink-0">
            <div className="w-8 h-8 rounded-full bg-[#71DED6]/20 flex items-center justify-center">
              <MessageSquare className="w-4 h-4 text-[#71DED6]" />
            </div>
            <div>
              <p className="text-xs font-semibold text-[#F4F4F8]">CleanBid SMS</p>
              <p className="text-[10px] text-[#71DED6]">Business Line</p>
            </div>
          </div>

          {/* Message viewport — fixed area, content anchored to bottom, clips upward */}
          <div className="relative flex-1 overflow-hidden">
            <div className="pointer-events-none absolute top-0 inset-x-0 h-8 z-10 bg-gradient-to-b from-[#0d0d14] to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-4 space-y-2.5">
              {msgs.slice(0, visible).map((msg, i) => (
                <div key={i} style={{ animation: "message-in 0.35s cubic-bezier(0.22,1,0.36,1) forwards" }}>
                  {msg.type === "in" && (
                    <div className="flex justify-start">
                      <div className="bg-[#1E1E2E] rounded-2xl rounded-tl-sm px-3 py-2 max-w-[85%]">
                        <p className="text-xs text-[#F4F4F8]">{msg.text}</p>
                      </div>
                    </div>
                  )}
                  {msg.type === "out" && (
                    <div className="flex justify-end">
                      <div className="bg-[#6B5CF6]/25 border border-[#6B5CF6]/20 rounded-2xl rounded-tr-sm px-3 py-2 max-w-[85%]">
                        <p className="text-xs text-[#F4F4F8]">{msg.text}</p>
                      </div>
                    </div>
                  )}
                  {msg.type === "quote" && (
                    <div className="flex justify-end">
                      <div className="bg-[#111118] border border-[#71DED6]/30 rounded-2xl rounded-tr-sm px-3 py-2.5 w-full max-w-[92%]">
                        <p className="text-[10px] text-[#8B8BA7] uppercase tracking-wider mb-1">Quote</p>
                        <p className="text-xs font-semibold text-[#F4F4F8]">{msg.service}</p>
                        <p className="text-[10px] text-[#8B8BA7]">{msg.size}</p>
                        <p className="text-lg font-bold text-[#71DED6] mt-1">{msg.price}</p>
                        <div className="mt-2 bg-[#71DED6] text-[#10151D] text-[10px] font-bold py-1.5 rounded-full text-center">
                          Accept Quote
                        </div>
                      </div>
                    </div>
                  )}
                  {msg.type === "status" && (
                    <div className="flex justify-center">
                      <span className="bg-[#71DED6]/15 border border-[#71DED6]/30 text-[#71DED6] text-[10px] font-semibold px-3 py-1 rounded-full flex items-center gap-1">
                        <Check className="w-3 h-3" /> {msg.text}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Main shared VerticalPage component ─────────────────── */
export default function VerticalPage({ config }) {
  const {
    verticalSlug, eyebrow, headline, headlineHighlight, sub,
    quoteCard, problems, features, testimonial, ctaBadge, heroImage,
  } = config;

  return (
    <div className="bg-[#0A0A0F]" data-testid={`vertical-page-${verticalSlug}`}>
      {/* HERO */}
      <section className="relative pt-28 md:pt-36 pb-24 overflow-hidden hero-grid">
        <div className="absolute inset-0 glow-cta pointer-events-none" />
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="space-y-6">
              <FadeUp>
                <span className="text-xs uppercase tracking-widest text-[#71DED6] font-semibold">{eyebrow}</span>
              </FadeUp>
              <FadeUp delay={70}>
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter leading-[1.05] text-[#F4F4F8]">
                  {headline}{" "}
                  <span className="text-gradient-green">{headlineHighlight}</span>
                </h1>
              </FadeUp>
              <FadeUp delay={140}>
                <p className="text-lg text-[#8B8BA7] leading-relaxed max-w-xl">{sub}</p>
              </FadeUp>
              <FadeUp delay={210}>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href={CALCOM_DEMO_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`${verticalSlug}-hero-cta`}
                    className="bg-[#71DED6] text-[#10151D] font-semibold px-8 py-3.5 rounded-full text-base hover:scale-[1.02] hover:shadow-glow-green-sm transition-transform duration-200 text-center"
                  >
                    Book a Demo
                  </a>
                  <Link
                    to="/how-it-works"
                    className="border border-[#1E1E2E] text-[#F4F4F8] font-semibold px-8 py-3.5 rounded-full text-base hover:bg-[#111118] transition-colors duration-200 flex items-center justify-center gap-2"
                  >
                    See How It Works <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </FadeUp>
              <FadeUp delay={280}>
                <p className="text-sm text-[#8B8BA7]">
                  <span className="text-[#F4F4F8] font-medium">Used by 200+ service businesses.</span>{" "}
                  No free trials. No fluff. Just results.
                </p>
              </FadeUp>
            </div>
            <FadeUp delay={180} className="flex justify-center lg:justify-end">
              {heroImage ? (
                <img
                  src={heroImage.src}
                  alt={heroImage.alt}
                  loading="lazy"
                  className="w-full rounded-2xl object-cover object-top h-[280px] lg:h-[400px]"
                  style={{ boxShadow: "0 0 0 1px rgba(113,222,214,0.2), 0 20px 60px rgba(0,0,0,0.4)" }}
                />
              ) : (
                <VerticalSMSMockup quoteCard={quoteCard} />
              )}
            </FadeUp>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="py-24 md:py-32" data-testid={`${verticalSlug}-problems`}>
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#F4F4F8] max-w-2xl">
              You're losing jobs you should be winning.
            </h2>
          </FadeUp>
          <FadeUp delay={60}>
            <p className="text-[#8B8BA7] text-lg mt-4 max-w-2xl leading-relaxed">
              Not because your work isn't good. Because the system isn't there — the quote was slow,
              the follow-up didn't happen, and the review never got asked for.
            </p>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
            {problems.map((card, i) => (
              <FadeUp key={i} delay={i * 80}>
                <CardHover>
                <div className="bg-[#111118] border border-[#1E1E2E] rounded-2xl p-7 transition-[box-shadow] duration-300 hover:shadow-card-hover">
                  <h3 className="text-lg font-semibold text-[#F4F4F8] mb-3">{card.title}</h3>
                  <p className="text-[#8B8BA7] text-sm leading-relaxed">{card.body}</p>
                </div>
                </CardHover>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-24 md:py-32 bg-[#111118]/30 border-t border-[#1E1E2E]" data-testid={`${verticalSlug}-features`}>
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <FadeUp className="text-center mb-14">
            <span className="text-xs uppercase tracking-widest text-[#71DED6] font-semibold">How CleanBid helps</span>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#F4F4F8] mt-3">
              Every tool you need. Nothing you don't.
            </h2>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {features.map((feat, i) => (
              <FadeUp key={i} delay={i * 80}>
                <CardHover className="h-full">
                <div className="bg-[#111118] border border-[#1E1E2E] rounded-2xl p-7 transition-[border-color] duration-300 hover:border-[#71DED6]/30 h-full">
                  <span className="text-xs uppercase tracking-widest text-[#71DED6] font-semibold">{feat.badge}</span>
                  <h3 className="text-xl font-bold tracking-tight text-[#F4F4F8] mt-3 mb-3">{feat.headline}</h3>
                  <p className="text-[#8B8BA7] text-sm leading-relaxed">{feat.body}</p>
                </div>
                </CardHover>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={300} className="text-center mt-10">
            <Link to="/features" className="text-sm text-[#8B8BA7] hover:text-[#F4F4F8] transition-colors underline underline-offset-4">
              See all features →
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* STATS + TESTIMONIAL */}
      <section className="py-24 md:py-32" data-testid={`${verticalSlug}-social-proof`}>
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-16 pb-16 border-b border-[#1E1E2E] text-center">
            {[
              { val: "3x", label: "faster quote response" },
              { val: "47%", label: "more booked jobs" },
              { val: "4.2x", label: "more reviews in 60 days" },
            ].map((s, i) => (
              <FadeUp key={i} delay={i * 80}>
                <p className="text-4xl md:text-5xl font-bold text-[#F4F4F8] tracking-tight">{s.val}</p>
                <p className="text-[#8B8BA7] text-sm mt-2">{s.label}</p>
              </FadeUp>
            ))}
          </div>
          <FadeUp>
            <div className="max-w-2xl mx-auto bg-[#111118] border border-[#71DED6]/30 rounded-2xl p-8 text-center shadow-glow-green">
              <div className="flex justify-center gap-1 mb-4">
                {[...Array(5)].map((_, j) => (
                  <Star key={j} className="w-5 h-5 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-[#F4F4F8] text-lg leading-relaxed mb-6">
                "{testimonial.quote}"
              </p>
              <div className="flex items-center justify-center gap-3">
                <img
                  src={testimonial.img}
                  alt={testimonial.name}
                  className="w-10 h-10 rounded-full object-cover bg-[#1E1E2E]"
                />
                <div className="text-left">
                  <p className="text-sm font-semibold text-[#F4F4F8]">{testimonial.name}</p>
                  <p className="text-xs text-[#8B8BA7]">{testimonial.role}</p>
                </div>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-28 bg-[#111118]/30 glow-cta" data-testid={`${verticalSlug}-cta`}>
        <div className="container mx-auto px-4 md:px-6 max-w-3xl text-center">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#F4F4F8] mb-4">
              {ctaBadge}
            </h2>
          </FadeUp>
          <FadeUp delay={80}>
            <p className="text-[#8B8BA7] text-lg mb-8 max-w-xl mx-auto">
              20 minutes. No sales pressure. We'll show you exactly how CleanBid works for your business.
            </p>
          </FadeUp>
          <FadeUp delay={160}>
            <a
              href={CALCOM_DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`${verticalSlug}-bottom-cta`}
              className="bg-[#71DED6] text-[#10151D] font-semibold px-12 py-4 rounded-full text-lg hover:scale-[1.02] hover:shadow-glow-green transition-transform duration-200 inline-block"
            >
              Book a Demo
            </a>
            <p className="text-sm text-[#8B8BA7] mt-4">
              No pressure. No commitment. Just results.
            </p>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
